// University of Massachusetts Lowell: public college page (#2839), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UMass Lowell's Common Data Set 2024-2025 (Office of Data, Analytics,
//    and Institutional Research), the newest edition on its Common Data Set
//    page; no 2025-2026 edition is posted. Departure: the admission counts
//    are for Fall 2024. C1 applicants, admits and enrollees with the
//    residency table (the Unknown column is 0 and left out), C8 (SAT or ACT
//    not required but considered, for fall 2026 applicants), C16 (rolling
//    notification from December 10), C21/C22 (no early decision plan; a
//    nonbinding, non-restrictive early action plan, with no counts). Its
//    C22 early action closing date (February 5) does not match the
//    admissions pages; the admissions pages are used for deadlines.
//  - UMass Lowell Undergraduate Admissions: Apply (the completed application
//    package "for Spring or Fall 2027", test scores or the No Test option,
//    music audition and art portfolio, and the first-year deadlines), and
//    Deadlines and Fees (the same deadlines; nursing applicants must apply
//    Early Action I or II), and the No Test Option FAQ. None publishes
//    decision dates.
//  - The Solution Center (financial aid), Undergraduate COA 2026-2027 (12
//    credits): the on-campus budget, in-state and out-of-state.
//  - College Scorecard API, school id 166513 (net price by family income,
//    in-state).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UMass Lowell Common Data Set 2024-2025',
        url: 'https://www.uml.edu/docs/CDS_2024-2025%20Final_tcm18-403507.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'UMass Lowell Undergraduate Admissions, Apply',
        url: 'https://www.uml.edu/admissions/apply/',
        read: '2026-10-01',
    },
    deadlinesPage: {
        label: 'UMass Lowell Undergraduate Admissions, Deadlines and Fees',
        url: 'https://www.uml.edu/admissions/apply/fees-deadlines.aspx',
        read: '2026-10-01',
    },
    noTest: {
        label: 'UMass Lowell Undergraduate Admissions, No Test Option FAQ',
        url: 'https://www.uml.edu/admissions/apply/no-test-option-faq.aspx',
        read: '2026-10-01',
    },
    coa: {
        label: 'UMass Lowell Solution Center, Undergraduate Cost of Attendance 2026-2027',
        url: 'https://www.uml.edu/thesolutioncenter/financial-aid/apply/undergrad-coa-odd-year.aspx',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?166513-University-of-Massachusetts-Lowell',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-massachusetts-lowell',
    kbId: 'university_of_massachusetts_lowell',
    name: 'University of Massachusetts Lowell',
    shortName: 'UMass Lowell',
    aliases: ['UML', 'UMass Lowell'],
    location: 'Lowell, MA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (uml.edu); decoration only.
    branding: {
        primary: '#1257D1',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_massachusetts_lowell_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 13676,
        admitted: 11345,
        enrolled: 2011,
        source: 'cds',
        where: 'section C1 (Fall 2024, the newest edition UMass Lowell posts)',
        residency: [
            { group: 'In-state', applied: 10271, admitted: 8503 },
            { group: 'Out-of-state', applied: 2783, admitted: 2296 },
            { group: 'International', applied: 622, admitted: 546 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UMass Lowell\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UMass Lowell has no early decision plan. Its early action plan is nonbinding and not restrictive. The Common Data Set gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most majors take the same application. Three ask for more:',
        rows: [
            { school: 'Nursing', requirement: 'You must apply by Early Action I or Early Action II.', source: 'deadlinesPage' },
            { school: 'Music', requirement: 'An entrance audition with the music department.' },
            { school: 'Art and Design', requirement: 'A portfolio.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 15' },
        rows: [
            { plan: 'Early Action I', deadline: 'November 5', decision: null, binding: false },
            { plan: 'Early Action II', deadline: 'January 15', decision: null, binding: false },
            { plan: 'Regular Decision', deadline: 'February 15', decision: null, binding: false },
        ],
        note: 'You can apply with the UMass Lowell application or the Common Application, with an application fee or a fee waiver. UMass Lowell\'s admissions pages do not publish decision dates.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'You can send SAT or ACT scores or choose the No Test Option on your application. UMass Lowell says applicants with a 1120 or higher on the SAT or 23 or higher on the ACT are good candidates to send scores, and No Test applicants should have a 3.0 GPA or higher. You are considered for merit scholarships either way. Some home-schooled applicants must send scores.',
        source: 'noTest',
        cdsLine: 'The 2024-2025 Common Data Set marks the SAT or ACT as not required for admission but considered if submitted, for fall 2026 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'noTest',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [17402, 36610] },
            { item: 'Fees', amounts: [850, 850] },
            { item: 'Housing (on campus, weighted average)', amounts: [11402, 11402] },
            { item: 'Food', amounts: [6080, 6080] },
            { item: 'Books', amounts: [1200, 1200] },
            { item: 'Loan fees', amounts: [68, 68] },
            { item: 'Transportation', amounts: [500, 500] },
            { item: 'Miscellaneous and personal', amounts: [900, 900] },
        ],
        totals: [38402, 57610],
        note: 'For a full-time undergraduate taking 12 or more semester hours and living on campus, fall and spring. On-campus housing runs from $9,990 to $13,540. New England students in the regional program pay $28,158 in tuition. Some colleges and majors add fees, and health insurance can add more.',
        source: 'coa',
        netPrice: {
            average: 17362,
            byIncome: [
                { income: '$0 to $30,000', amount: 9180 },
                { income: '$30,001 to $48,000', amount: 9988 },
                { income: '$48,001 to $75,000', amount: 10484 },
                { income: '$75,001 to $110,000', amount: 16122 },
                { income: 'Over $110,000', amount: 27079 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UMass Lowell\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
